import React from 'react'
import type { MovingRect } from './utils/canvasAlignment'
import { getCanvasNoteColorHex } from './utils/canvasNoteStyle'
import { getMemoColor } from './utils/canvasMemoStyle'
import './CanvasSpawnPreview.scss'

export interface CanvasSpawnPreviewProps {
  /** Wo der neue Zettel landet — in Canvas-Koordinaten (siehe `getSpawnRect`). */
  rect: MovingRect
  kind: 'note' | 'memo'
  /** Die Farbe, die der neue Zettel erbt. */
  colorKey: string | null
  /**
   * Der Klick ist gefallen — das Bild hält nur noch den Platz, bis der echte
   * Zettel eintrifft. Es verliert dafür seine Transparenz.
   */
  committed?: boolean
}

/**
 * Das Bild des Zettels, der über einen **Anlegepunkt** gleich entsteht —
 * halbtransparent an genau der Stelle, an der der Klick ihn anlegt.
 *
 * Anders als `CanvasPlacementPreview` (das Bild am Zeiger beim Absetzen)
 * liegt es im `ViewportPortal`, in Canvas-Koordinaten: Es gehört zum Zettel,
 * nicht zum Zeiger, und zoomt mit der Fläche. Die Optik teilen sich beide
 * (`canvasPreviewSurface.scss`).
 *
 * **Nach dem Klick bleibt es stehen** (`committed`) und wird undurchsichtig,
 * bis der echte Zettel da ist. Es ist dann kein Versprechen mehr, sondern ein
 * Platzhalter — und der Übergang zum echten Zettel fällt nicht auf.
 */
export const CanvasSpawnPreview: React.FC<CanvasSpawnPreviewProps> = ({ rect, kind, colorKey, committed = false }) => {
  const textColor = kind === 'memo' ? getMemoColor(colorKey) : null
  const fill = textColor ? textColor.surface : getCanvasNoteColorHex(colorKey)

  return (
    <div
      className={
        `canvas-spawn-preview canvas-spawn-preview--${kind}`
        + (committed ? ' canvas-spawn-preview--committed' : '')
      }
      aria-hidden="true"
      style={{
        left: rect.x,
        top: rect.y,
        width: rect.width,
        height: rect.height,
        '--preview-fill': fill,
        ...(textColor ? {
          '--preview-border': textColor.border,
          // Der **Streifen** — wie am Bild beim Absetzen. Beide zeigen
          // dasselbe, weil sie dasselbe versprechen.
          '--preview-stripe': textColor.stripe,
          '--preview-stripe-line': textColor.stripeLine,
        } : {}),
      } as React.CSSProperties}
    />
  )
}
